import { ImagePlus, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { fr } from '@/i18n/fr'

/** Mirrors `recipes.photos.MAX_UPLOAD_BYTES`. Checked here so the refusal arrives before the
 *  upload rather than after it, which on a phone connection is a slow way to learn. The
 *  server still enforces it — this is the compiler saying it first, as with F-15. */
const MAX_BYTES = 15 * 1024 * 1024

// No browser other than Safari can paint a HEIC/HEIF blob URL in an <img>, so an iPhone photo
// — the common case — would preview as blank until the server's derived JPEG comes back after
// submit. `.heic`/`.heif` matched by extension too: some Android pickers report neither MIME
// type (NFR-20's same problem, on the preview path instead of the accept filter).
const HEIC_EXTENSION = /\.hei[cf]$/i

function isHeic(file: File): boolean {
  return file.type === 'image/heic' || file.type === 'image/heif' || HEIC_EXTENSION.test(file.name)
}

async function previewSource(file: File): Promise<Blob> {
  if (!isHeic(file)) return file
  const { default: heic2any } = await import('heic2any')
  const result = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.8 })
  return Array.isArray(result) ? result[0] : result
}

interface Props {
  /** The stored photo, if the recipe already has one. */
  current: string | null
  /** A file picked in this session and not yet uploaded. */
  picked: File | null
  onPick: (file: File | null) => void
  /** True once the existing photo is marked for removal on save. */
  removed: boolean
  onRemove: () => void
}

/**
 * The photo half of the editor (F-20). It picks a file and nothing else — the upload happens
 * after the recipe is saved, because a recipe being created has no id to attach a file to.
 */
export function PhotoField({ current, picked, onPick, removed, onRemove }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [decoding, setDecoding] = useState(false)

  useEffect(() => {
    if (!picked) {
      setPreviewUrl(null)
      setDecoding(false)
      return
    }
    let cancelled = false
    let url: string | null = null
    setDecoding(isHeic(picked))

    previewSource(picked)
      .then((source) => {
        if (cancelled) return
        url = URL.createObjectURL(source)
        setPreviewUrl(url)
      })
      .catch(() => {
        // A HEIC decode failure leaves no local preview — the upload and the server's own
        // derived JPEGs (NFR-41) are unaffected, so this only costs the preview, not the save.
        if (!cancelled) setPreviewUrl(null)
      })
      .finally(() => {
        if (!cancelled) setDecoding(false)
      })

    return () => {
      cancelled = true
      // An object URL pins the file in memory until it is revoked.
      if (url) URL.revokeObjectURL(url)
    }
  }, [picked])

  const shown = previewUrl ?? (removed ? null : current)

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    // Let the same file be picked again after a removal.
    event.target.value = ''
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setError(fr.recipeEdit.photo.notAnImage)
      return
    }
    if (file.size > MAX_BYTES) {
      setError(fr.recipeEdit.photo.tooLarge(MAX_BYTES / (1024 * 1024)))
      return
    }
    setError(null)
    onPick(file)
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium">{fr.recipeEdit.photo.label}</span>

      {shown && (
        <img
          src={shown}
          alt=""
          className="aspect-[16/9] w-full rounded-2xl border border-stone-200 object-cover dark:border-stone-800"
        />
      )}
      {decoding && !shown && (
        <div className="flex aspect-[16/9] w-full items-center justify-center rounded-2xl border border-stone-200 text-sm text-stone-500 dark:border-stone-800">
          {fr.recipeEdit.photo.decodingPreview}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={() => inputRef.current?.click()}>
          <ImagePlus className="h-4 w-4" />
          {shown ? fr.recipeEdit.photo.replace : fr.recipeEdit.photo.choose}
        </Button>
        {shown && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setError(null)
              onPick(null)
              onRemove()
            }}
            className="text-stone-500"
          >
            <Trash2 className="h-4 w-4" />
            {fr.recipeEdit.photo.remove}
          </Button>
        )}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* No `capture` attribute on purpose: it forces the camera and hides the library on
          iOS, and the common case is a photo taken ten minutes ago, not one being taken
          now. `.heic`/`.heif` are named explicitly because some Android pickers do not
          match them against `image/*` (NFR-20). */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,.heic,.heif"
        onChange={handleChange}
        className="hidden"
        aria-label={fr.recipeEdit.photo.choose}
      />
    </div>
  )
}
